/**
 * LegalDisclaimer - Avertissements juridiques contextuels
 * Conforme au CDC Juridique v1.2 §9.3
 * Variants: search, substance, duerp, unit
 */

interface LegalDisclaimerProps {
  variant: 'search' | 'substance' | 'duerp' | 'unit'
  className?: string
}

// Textes par variant (CDC v1.2 §9.3)
const disclaimerContent = {
  search: {
    title: null,
    text: `Les données présentées sont issues de sources officielles et fournies à titre indicatif. Vérifiez leur actualité auprès des sources primaires avant utilisation professionnelle.`,
    style: 'minimal',
  },
  substance: {
    title: 'Avertissement',
    text: `Cette fiche synthétise des données issues de sources officielles. Elle ne constitue pas un conseil personnalisé et ne se substitue pas aux fiches de données de sécurité (FDS) des produits utilisés ni à l'expertise d'un professionnel qualifié.

Les données sont mises à jour périodiquement mais ne constituent pas une veille réglementaire exhaustive. Vérifiez l'actualité des informations critiques auprès des sources primaires.`,
    footer: 'Sources : INRS • ANSES • ECHA • AGRITOX',
    style: 'warning',
  },
  duerp: {
    title: 'Information importante',
    text: `Le Document Unique d'Évaluation des Risques Professionnels (DUERP) est une obligation légale de l'employeur. L'utilisation de RISK-DATA ne saurait garantir la conformité réglementaire de votre document.

Les données toxicologiques sont mises à jour périodiquement mais ne constituent pas une veille réglementaire exhaustive. Vérifiez l'actualité des valeurs limites auprès des sources officielles.`,
    style: 'info',
  },
  unit: {
    title: null,
    text: `L'évaluation des risques présentée est indicative et ne constitue pas un conseil personnalisé. Elle doit être complétée par une analyse terrain et l'expertise de professionnels de la prévention.

Vérifiez l'actualité des valeurs limites auprès des sources officielles.`,
    style: 'info',
  },
}

export default function LegalDisclaimer({
  variant,
  className = '',
}: LegalDisclaimerProps) {
  const content = disclaimerContent[variant]

  // Style minimal pour la recherche
  if (content.style === 'minimal') {
    return (
      <div className={`flex items-start gap-2 text-xs text-gray-500 ${className}`}>
        <svg
          className="w-4 h-4 flex-shrink-0 mt-0.5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <p>{content.text}</p>
      </div>
    )
  }

  // Style warning (jaune/ambre)
  if (content.style === 'warning') {
    return (
      <div
        className={`bg-amber-50 border border-amber-200 rounded-lg p-4 ${className}`}
      >
        <div className="flex items-start gap-3">
          <svg
            className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <div className="flex-1">
            {content.title && (
              <h4 className="text-sm font-medium text-amber-800 mb-1">
                {content.title}
              </h4>
            )}
            <div className="text-sm text-amber-700 whitespace-pre-line">
              {content.text}
            </div>
            {'footer' in content && content.footer && (
              <p className="text-xs text-amber-600 mt-3 font-medium">
                {content.footer}
              </p>
            )}
          </div>
        </div>
      </div>
    )
  }

  // Style info (bleu)
  return (
    <div
      className={`bg-blue-50 border border-blue-200 rounded-lg p-4 ${className}`}
    >
      <div className="flex items-start gap-3">
        <svg
          className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <div className="flex-1">
          {content.title && (
            <h4 className="text-sm font-medium text-blue-800 mb-1">
              {content.title}
            </h4>
          )}
          <div className="text-sm text-blue-700 whitespace-pre-line">
            {content.text}
          </div>
        </div>
      </div>
    </div>
  )
}
